import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Newspaper, Plus, Pencil, Trash2, ImagePlus, X, Lock, Sparkles, Wand2, ExternalLink, Share2 } from "lucide-react";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", ai_generated: false };
const TONES = [
  { v: "chaleureux", l: "Chaleureux" }, { v: "dynamique", l: "Dynamique" },
  { v: "convivial", l: "Convivial" }, { v: "institutionnel", l: "Institutionnel" },
];

export default function ClubNews() {
  const ctx = useOutletContext() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("chaleureux");
  const [aiTextBusy, setAiTextBusy] = useState(false);
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const imgRef = useRef();
  const active = ctx.club ? ctx.club.status === "active" : true;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/clubs/me/news"); setItems(data.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const openNew = () => { setForm(EMPTY); setTopic(""); setEditing({}); };
  const openEdit = (n) => {
    setForm({ title: n.title || "", excerpt: n.excerpt || "", content: n.content || "", cover_image: n.cover_image || "", ai_generated: !!n.ai_generated });
    setEditing(n);
  };
  const close = () => { setEditing(null); setForm(EMPTY); setTopic(""); };

  const pickImage = async () => {
    const f = imgRef.current?.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image trop lourde (max 6 Mo)."); return; }
    set("cover_image", await fileToDataUrl(f));
  };

  const genText = async () => {
    if (!topic || topic.length < 2) { toast.error("Décrivez le sujet de votre actualité."); return; }
    setAiTextBusy(true);
    try {
      const { data } = await api.post("/clubs/me/ai/news", { topic, tone });
      setForm((s) => ({ ...s, title: data.title || s.title, excerpt: data.excerpt || s.excerpt, content: data.content || s.content, ai_generated: true }));
      toast.success("Brouillon rédigé par l'IA ! Relisez et ajustez.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération impossible, réessayez."); }
    finally { setAiTextBusy(false); }
  };

  const genImage = async () => {
    const subject = form.title || topic;
    if (!subject) { toast.error("Donnez d'abord un titre ou un sujet."); return; }
    setAiImgBusy(true);
    try {
      const { data } = await api.post("/clubs/me/ai/image", { subject, style: "photo" });
      set("cover_image", data.image_url);
      toast.success("Image générée par l'IA !");
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération d'image impossible."); }
    finally { setAiImgBusy(false); }
  };

  const save = async () => {
    if (!form.title || form.title.length < 2) { toast.error("Le titre est requis."); return; }
    if (!form.content || form.content.trim().length < 1) { toast.error("Le contenu est requis."); return; }
    setBusy(true);
    try {
      if (editing && editing.id) { await api.patch(`/clubs/me/news/${editing.id}`, form); toast.success("Actualité mise à jour !"); }
      else { await api.post("/clubs/me/news", form); toast.success("Actualité publiée !"); }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur à l'enregistrement."); }
    finally { setBusy(false); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Supprimer « ${n.title} » ?`)) return;
    try { await api.delete(`/clubs/me/news/${n.id}`); toast.success("Actualité supprimée."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <div className="max-w-4xl" data-testid="club-news">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Mes actualités</h1>
          <p className="text-slate-500 text-sm mt-1">Publiez vos actus et laissez l'assistant IA rédiger un premier jet.</p>
        </div>
        <Button onClick={openNew} disabled={!active} className="btn-shine text-white" data-testid="club-news-new"><Plus size={16} className="mr-1" /> Nouvelle actualité</Button>
      </div>

      {!active && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex items-center gap-2" data-testid="club-news-locked">
          <Lock size={16} /> La publication d'actualités sera débloquée dès la validation de votre page.
        </div>
      )}

      {editing && (
        <div className="mt-5 space-y-4" data-testid="club-news-form">
          {/* Assistant IA */}
          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 font-heading font-bold text-violet-800"><Wand2 size={18} /> Assistant IA de rédaction</div>
            <p className="text-sm text-violet-700/80 mt-1">Décrivez votre sujet, l'IA propose un titre, un résumé et le texte.</p>
            <div className="mt-3 grid sm:grid-cols-[1fr_auto_auto] gap-2">
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ex : tournoi de printemps, appel aux bénévoles…" data-testid="club-news-ai-topic" />
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="sm:w-44" data-testid="club-news-ai-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => (<SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>))}</SelectContent>
              </Select>
              <Button onClick={genText} disabled={aiTextBusy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="club-news-ai-generate">
                <Sparkles size={15} className="mr-1" /> {aiTextBusy ? "Rédaction…" : "Rédiger"}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h2 className="font-heading font-bold">{editing.id ? "Modifier l'actualité" : "Nouvelle actualité"}</h2>
            <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="club-news-title" /></div>
            <div><Label>Résumé (accroche)</Label><Input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></div>
            <div>
              <Label>Contenu * <span className="text-slate-400 font-normal">(utilisez « ## » pour un sous-titre)</span></Label>
              <Textarea rows={10} value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="club-news-content" />
            </div>
            <div>
              <Label>Image de couverture</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-28 h-16 rounded-xl bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                  {form.cover_image ? <img src={form.cover_image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="text-slate-300" />}
                </div>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
                <Button type="button" variant="outline" size="sm" onClick={() => imgRef.current?.click()}><ImagePlus size={14} className="mr-1" /> Choisir</Button>
                <Button type="button" variant="outline" size="sm" onClick={genImage} disabled={aiImgBusy} data-testid="club-news-ai-image"><Sparkles size={14} className="mr-1" /> {aiImgBusy ? "Génération…" : "Générer (IA)"}</Button>
                {form.cover_image && <button type="button" onClick={() => set("cover_image", "")} className="text-slate-400 hover:text-rose-600"><X size={16} /></button>}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={close}>Annuler</Button>
              <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="club-news-save">{busy ? "Publication…" : "Publier"}</Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="club-news-empty">
            <Newspaper size={30} className="mx-auto text-slate-300 mb-2" />
            Aucune actualité pour l'instant.
          </div>
        ) : items.map((n) => (
          <div key={n.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`club-news-row-${n.id}`}>
            <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
              {n.cover_image ? <img src={n.cover_image} alt="" className="w-full h-full object-cover" /> : <Newspaper className="text-slate-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                {n.title}
                {n.ai_generated && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700"><Sparkles size={10} /> IA</span>}
              </div>
              {n.excerpt && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{n.excerpt}</div>}
              {n.shares > 0 && <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1" data-testid={`club-news-shares-${n.id}`}><Share2 size={11} /> {n.shares} partage{n.shares > 1 ? "s" : ""}</div>}
            </div>
            <div className="flex gap-1 shrink-0">
              {ctx.club?.status === "active" && ctx.club?.slug && (
                <a href={`/clubs/${ctx.club.slug}/actualites/${n.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-indigo-700" title="Voir en ligne"><ExternalLink size={15} /></a>
              )}
              <Button variant="ghost" size="sm" onClick={() => openEdit(n)} data-testid={`club-news-edit-${n.id}`}><Pencil size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(n)} className="text-rose-600 hover:text-rose-700" data-testid={`club-news-delete-${n.id}`}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
